/* Customer Communication Drafts panel — Phase 3
 * Renders one editable draft per affected customer, with copy/print/export.
 * Drafts are TEXT ONLY. The system never sends anything to customers.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Copy, Printer, FileDown, Mail, AlertTriangle } from 'lucide-react';
import { exportCsv } from './ReportLayout';

const PLACEHOLDER_EXPLANATION =
  '[Replace this paragraph with a plain-language explanation of the issue. Keep it factual, avoid blame language, and do not commit to a position before QA/legal/insurer review.]';
const PLACEHOLDER_ACTION =
  '[Replace with the action you are asking the customer to take, e.g. "Please quarantine the listed batches and confirm receipt of this notice by [date]".]';

function defaultBody(d) {
  const today = new Date().toISOString().slice(0, 10);
  return (
`Subject: Traceability event notification — ${d.event_ref || 'TE-DRAFT'}

Dear ${d.contact_person || '[Contact name]'},

We are writing to inform you of a traceability event related to a recent dispatch:

  Dispatch reference:  ${d.dispatch_ref || '—'}
  Dispatch date:       ${d.dispatch_date || '—'}
  Product:             ${d.affected_product || '—'}
  Cheese batch:        ${d.affected_batch || '—'}
  Quantity supplied:   ${d.quantity_supplied ?? '—'}

${d.explanation || PLACEHOLDER_EXPLANATION}

Requested action
${d.requested_action || PLACEHOLDER_ACTION}

We will keep you updated as the investigation progresses.
For any urgent questions please contact: ${d.contact_person || '[contact]'}.

Kind regards,
${d.contact_person || '[Your name]'}
${d.company || 'Keystone Demo Producer'}
Date: ${today}
Event ref: ${d.event_ref || 'TE-DRAFT'}`
  );
}

function buildDraftsFromRecall(data, eventRef) {
  // Flatten downstream into one draft per (customer, dispatch).
  const drafts = [];
  for (const row of (data?.downstream || [])) {
    const c = row.customer || {};
    const d = row.dispatch || {};
    const lines = d.lines || [];
    if (!lines.length) {
      drafts.push({
        customer_id: c.id || '',
        customer_name: c.name || '—',
        dispatch_ref: d.reference || d.dispatch_ref || d.id?.slice(0, 8) || '',
        dispatch_date: d.dispatch_date || '',
        affected_product: '',
        affected_batch: '',
        quantity_supplied: '',
        explanation: '',
        requested_action: '',
        contact_person: '',
        event_ref: eventRef || '',
      });
      continue;
    }
    for (const ln of lines) {
      drafts.push({
        customer_id: c.id || '',
        customer_name: c.name || '—',
        dispatch_ref: d.reference || d.dispatch_ref || d.id?.slice(0, 8) || '',
        dispatch_date: d.dispatch_date || '',
        affected_product: ln.product || ln.product_name || '',
        affected_batch: ln.batch_number || '',
        quantity_supplied: ln.quantity ?? ln.cases ?? '',
        explanation: '',
        requested_action: '',
        contact_person: '',
        event_ref: eventRef || '',
      });
    }
  }
  return drafts;
}

export default function CustomerDraftsPanel({ recallData, eventRef, onChange }) {
  const [drafts, setDrafts] = useState([]);
  const [generated, setGenerated] = useState(false);

  // Re-seed drafts whenever the recall data or eventRef changes — but only if user hasn't generated yet
  useEffect(() => {
    if (!generated) return;
    // when generated, keep existing edits; only re-seed if drafts are empty
    if (drafts.length === 0) {
      setDrafts(buildDraftsFromRecall(recallData, eventRef));
    }
  }, [recallData, eventRef]); // eslint-disable-line

  useEffect(() => { onChange && onChange(drafts); }, [drafts]); // eslint-disable-line

  const handleGenerate = () => {
    const built = buildDraftsFromRecall(recallData, eventRef);
    if (!built.length) {
      toast.info('No affected customers / dispatches to draft for yet. Run the report first.');
      return;
    }
    setDrafts(built);
    setGenerated(true);
    toast.success(`${built.length} draft${built.length > 1 ? 's' : ''} generated. Each draft must be reviewed before sending.`);
  };

  const updateField = (i, key, val) => setDrafts(arr => arr.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
  const renderBody = (d) => d.draft_body || defaultBody(d);

  const copyOne = async (d) => {
    try {
      await navigator.clipboard.writeText(renderBody(d));
      toast.success('Draft copied to clipboard');
    } catch (e) { toast.error('Copy failed'); }
  };

  const printOne = (d) => {
    const w = window.open('', 'print-draft', 'width=800,height=900');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    w.document.write(`<!doctype html><html><head><title>Draft — ${d.customer_name}</title>
      <style>body{font-family:-apple-system,system-ui,sans-serif;padding:32px;max-width:760px;margin:0 auto;color:#111}
      pre{white-space:pre-wrap;font-family:inherit;font-size:14px;line-height:1.55}
      .banner{background:#fef3c7;border:1px solid #fbbf24;padding:10px 14px;border-radius:6px;margin-bottom:18px;font-size:12px}
      h1{font-size:18px;margin:0 0 4px}
      </style></head><body>
      <div class="banner">⚠ DRAFT — This is a draft communication and must be reviewed before sending.</div>
      <h1>Customer draft — ${d.customer_name}</h1>
      <pre>${renderBody(d).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</pre>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  const exportAllText = () => {
    if (!drafts.length) return;
    const sep = '\n\n' + '='.repeat(72) + '\n\n';
    const banner = 'DRAFT — This is a draft communication and must be reviewed before sending.\n';
    const all = drafts.map(d => banner + '\n' + renderBody(d)).join(sep);
    const blob = new Blob([all], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `customer-drafts-${eventRef || 'event'}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const exportAllCsv = () => {
    if (!drafts.length) return;
    exportCsv(`customer-drafts-${eventRef || 'event'}.csv`, drafts, [
      { key: 'customer_name', label: 'Customer' },
      { key: 'dispatch_ref', label: 'Dispatch' },
      { key: 'dispatch_date', label: 'Dispatch date' },
      { key: 'affected_product', label: 'Product' },
      { key: 'affected_batch', label: 'Batch' },
      { key: 'quantity_supplied', label: 'Quantity' },
      { key: 'explanation', label: 'Explanation' },
      { key: 'requested_action', label: 'Requested action' },
      { key: 'contact_person', label: 'Contact person' },
    ]);
  };

  return (
    <section className="mt-6" data-testid="drafts-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3 print:hidden">
        <h3 className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4" />Customer Communication Drafts</h3>
        <div className="flex gap-2">
          {!drafts.length && <Button onClick={handleGenerate} data-testid="btn-generate-drafts"><Mail className="w-4 h-4 mr-1" />Generate Customer Drafts</Button>}
          {drafts.length > 0 && <>
            <Button variant="outline" size="sm" onClick={exportAllText} data-testid="btn-export-drafts-txt"><FileDown className="w-4 h-4 mr-1" />Export .txt</Button>
            <Button variant="outline" size="sm" onClick={exportAllCsv} data-testid="btn-export-drafts-csv"><FileDown className="w-4 h-4 mr-1" />Export CSV</Button>
            <Button variant="outline" size="sm" onClick={handleGenerate}>Regenerate</Button>
          </>}
        </div>
      </div>

      {drafts.length === 0 && <Card><CardContent className="pt-5 text-sm text-muted-foreground italic">
        Click <span className="font-medium not-italic">Generate Customer Drafts</span> after running a recall report to produce one editable draft per affected customer. Drafts are text-only — the system never sends anything to customers automatically.
      </CardContent></Card>}

      {drafts.length > 0 && <div className="bg-amber-50 border border-amber-300 rounded-md px-4 py-2.5 mb-3 text-sm text-amber-900 flex items-center gap-2 print:bg-transparent print:border-amber-600" data-testid="drafts-safeguard-banner">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        This is a draft communication and must be reviewed before sending. No emails will be sent automatically — copy, print or export only.
      </div>}

      <div className="space-y-4">
        {drafts.map((d, i) => (
          <Card key={i} className="print:break-inside-avoid" data-testid={`draft-card-${i}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Draft for</div>
                  <h4 className="font-semibold text-lg">{d.customer_name}</h4>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">DRAFT — review required</Badge>
                  <Button size="sm" variant="outline" onClick={() => copyOne(d)} data-testid={`btn-copy-draft-${i}`}><Copy className="w-4 h-4 mr-1" />Copy</Button>
                  <Button size="sm" variant="outline" onClick={() => printOne(d)} data-testid={`btn-print-draft-${i}`}><Printer className="w-4 h-4 mr-1" />Print</Button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 mb-3 text-sm">
                <div><Label className="text-xs">Dispatch</Label><Input value={d.dispatch_ref || ''} onChange={e => updateField(i, 'dispatch_ref', e.target.value)} /></div>
                <div><Label className="text-xs">Dispatch date</Label><Input value={d.dispatch_date || ''} onChange={e => updateField(i, 'dispatch_date', e.target.value)} /></div>
                <div><Label className="text-xs">Product</Label><Input value={d.affected_product || ''} onChange={e => updateField(i, 'affected_product', e.target.value)} /></div>
                <div><Label className="text-xs">Batch</Label><Input value={d.affected_batch || ''} onChange={e => updateField(i, 'affected_batch', e.target.value)} /></div>
                <div><Label className="text-xs">Quantity</Label><Input value={d.quantity_supplied ?? ''} onChange={e => updateField(i, 'quantity_supplied', e.target.value)} /></div>
                <div><Label className="text-xs">Contact person</Label><Input value={d.contact_person || ''} onChange={e => updateField(i, 'contact_person', e.target.value)} placeholder="QA lead / GM" /></div>
              </div>

              <div className="grid md:grid-cols-2 gap-3 mb-3">
                <div><Label className="text-xs">Plain-language explanation</Label>
                  <Textarea rows={3} value={d.explanation || ''} onChange={e => updateField(i, 'explanation', e.target.value)} placeholder={PLACEHOLDER_EXPLANATION} data-testid={`draft-explanation-${i}`} />
                </div>
                <div><Label className="text-xs">Requested customer action</Label>
                  <Textarea rows={3} value={d.requested_action || ''} onChange={e => updateField(i, 'requested_action', e.target.value)} placeholder={PLACEHOLDER_ACTION} data-testid={`draft-action-${i}`} />
                </div>
              </div>

              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Preview rendered draft text</summary>
                <pre className="mt-2 text-xs bg-zinc-50 p-3 rounded border whitespace-pre-wrap font-mono">{renderBody(d)}</pre>
              </details>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
